import React from 'react'

/**
 * Markdown simples do texto do oneflow, montado em elementos React: nada vira HTML cru.
 * Cobre títulos, listas, tabelas, citações, blocos de código, negrito, itálico, código e links http(s).
 */

const linkSeguro = (url: string) => /^https?:\/\//i.test(url)

function inline(texto: string, chave: string): React.ReactNode[] {
  const saida: React.ReactNode[] = []
  const padrao = /(`[^`]+`)|(\*\*[^*]+\*\*)|(__[^_]+__)|(\*[^*\s][^*]*\*)|(_[^_\s][^_]*_)|(\[[^\]]+\]\([^)\s]+\))/g
  let ultimo = 0
  let m: RegExpExecArray | null
  let i = 0
  while ((m = padrao.exec(texto)) !== null) {
    if (m.index > ultimo) saida.push(texto.slice(ultimo, m.index))
    const t = m[0]
    const k = `${chave}-${i++}`
    if (m[1]) saida.push(<code key={k} className="of-md-code">{t.slice(1, -1)}</code>)
    else if (m[2] || m[3]) saida.push(<strong key={k}>{inline(t.slice(2, -2), k)}</strong>)
    else if (m[4] || m[5]) saida.push(<em key={k}>{inline(t.slice(1, -1), k)}</em>)
    else {
      const [, rotulo, url] = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(t) || []
      saida.push(linkSeguro(url) ? <a key={k} href={url} target="_blank" rel="noopener noreferrer">{rotulo}</a> : rotulo)
    }
    ultimo = m.index + t.length
  }
  if (ultimo < texto.length) saida.push(texto.slice(ultimo))
  return saida
}

const celulas = (linha: string) => linha.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim())
const separadorTabela = (linha: string) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(linha)

export default function Markdown({ texto }: { texto: string }) {
  const linhas = (texto || '').replace(/\r\n/g, '\n').split('\n')
  const blocos: React.ReactNode[] = []
  let i = 0
  let n = 0

  while (i < linhas.length) {
    const linha = linhas[i]
    const k = `b${n++}`

    if (!linha.trim()) { i++; continue }

    if (/^\s*```/.test(linha)) {
      const corpo: string[] = []
      i++
      while (i < linhas.length && !/^\s*```/.test(linhas[i])) corpo.push(linhas[i++])
      i++
      blocos.push(<pre key={k} className="of-md-pre">{corpo.join('\n')}</pre>)
      continue
    }

    const titulo = /^(#{1,6})\s+(.*)$/.exec(linha)
    if (titulo) {
      const nivel = Math.min(titulo[1].length + 2, 6)
      blocos.push(React.createElement(`h${nivel}`, { key: k, className: 'of-md-titulo' }, inline(titulo[2], k)))
      i++
      continue
    }

    if (linha.includes('|') && i + 1 < linhas.length && separadorTabela(linhas[i + 1])) {
      const cabecalho = celulas(linha)
      const corpo: string[][] = []
      i += 2
      while (i < linhas.length && linhas[i].includes('|') && linhas[i].trim()) corpo.push(celulas(linhas[i++]))
      blocos.push(
        <div key={k} className="of-tabela-rolagem">
          <table className="of-tabela">
            <thead><tr>{cabecalho.map((c, j) => <th key={j}>{inline(c, `${k}h${j}`)}</th>)}</tr></thead>
            <tbody>{corpo.map((l, r) => <tr key={r}>{l.map((c, j) => <td key={j}>{inline(c, `${k}${r}-${j}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      )
      continue
    }

    if (/^\s*([-*+]|\d+[.)])\s+/.test(linha)) {
      const ordenada = /^\s*\d+[.)]\s+/.test(linha)
      const itens: string[] = []
      while (i < linhas.length && /^\s*([-*+]|\d+[.)])\s+/.test(linhas[i])) {
        itens.push(linhas[i].replace(/^\s*([-*+]|\d+[.)])\s+/, ''))
        i++
      }
      const lista = itens.map((t, j) => <li key={j}>{inline(t, `${k}-${j}`)}</li>)
      blocos.push(ordenada ? <ol key={k} className="of-md-lista">{lista}</ol> : <ul key={k} className="of-md-lista">{lista}</ul>)
      continue
    }

    if (/^\s*>/.test(linha)) {
      const citacao: string[] = []
      while (i < linhas.length && /^\s*>/.test(linhas[i])) citacao.push(linhas[i++].replace(/^\s*>\s?/, ''))
      blocos.push(<blockquote key={k} className="of-md-citacao">{inline(citacao.join(' '), k)}</blockquote>)
      continue
    }

    const paragrafo: string[] = []
    while (i < linhas.length && linhas[i].trim() && !/^\s*(```|#{1,6}\s|[-*+]\s|\d+[.)]\s|>)/.test(linhas[i]) &&
           !(linhas[i].includes('|') && i + 1 < linhas.length && separadorTabela(linhas[i + 1]))) {
      paragrafo.push(linhas[i++])
    }
    if (paragrafo.length === 0) { paragrafo.push(linhas[i++]) }
    blocos.push(
      <p key={k} className="of-md-p">
        {paragrafo.map((p, j) => <React.Fragment key={j}>{j > 0 && <br />}{inline(p, `${k}-${j}`)}</React.Fragment>)}
      </p>
    )
  }

  return <div className="of-md">{blocos}</div>
}
